import { useEffect, useState, useMemo } from "react";

export type WatermarkIdentity = "email" | "phone" | "both" | "id_only";

interface ForensicWatermarkProps {
  email?: string;
  phone?: string;
  fallback?: string;
  enabled?: boolean;
  /** Center diagonal flash (large, faint) */
  flashEnabled?: boolean;
  flashIntervalSeconds?: number;
  /** Top-left small corner flash interval (0 = disabled) */
  cornerTopLeftIntervalSeconds?: number;
  /** Bottom-right small corner flash interval (0 = disabled) */
  cornerBottomRightIntervalSeconds?: number;
  identity?: WatermarkIdentity;
}

/**
 * Forensic watermark overlay with independent flashing corners + center.
 * Each element flashes on its own interval to make cropping/clean-up much harder.
 */
export const ForensicWatermark = ({
  email, phone, fallback,
  enabled = true,
  flashEnabled = true,
  flashIntervalSeconds = 25,
  cornerTopLeftIntervalSeconds = 17,
  cornerBottomRightIntervalSeconds = 23,
  identity = "both",
}: ForensicWatermarkProps) => {
  const [centerVisible, setCenterVisible] = useState(!flashEnabled);
  const [tlVisible, setTlVisible] = useState(false);
  const [brVisible, setBrVisible] = useState(false);

  const label = useMemo(() => {
    const parts: string[] = [];
    if (identity === "email" && email) parts.push(email);
    else if (identity === "phone" && phone) parts.push(phone);
    else if (identity === "id_only" && fallback) parts.push(fallback);
    else if (identity === "both") {
      if (email) parts.push(email);
      if (phone) parts.push(phone);
    }
    if (parts.length === 0 && fallback) parts.push(fallback);
    return parts.join(" • ") || "Protected Content";
  }, [email, phone, fallback, identity]);

  // Short label for corners (avoids overflow on small screens)
  const shortLabel = useMemo(() => {
    if (identity === "phone" && phone) return phone;
    if (email) return email;
    if (phone) return phone;
    return label;
  }, [email, phone, identity, label]);

  // Center flash
  useEffect(() => {
    if (!enabled || !flashEnabled) {
      setCenterVisible(enabled);
      return;
    }
    const ms = Math.max(10, flashIntervalSeconds) * 1000;
    const id = setInterval(() => {
      setCenterVisible(true);
      setTimeout(() => setCenterVisible(false), 3000);
    }, ms);
    return () => clearInterval(id);
  }, [enabled, flashEnabled, flashIntervalSeconds]);

  // Top-left corner flash (independent timing)
  useEffect(() => {
    if (!enabled || cornerTopLeftIntervalSeconds <= 0) { setTlVisible(false); return; }
    const ms = Math.max(5, cornerTopLeftIntervalSeconds) * 1000;
    // Stagger first flash so it doesn't sync with the center
    const initial = setTimeout(() => {
      setTlVisible(true);
      setTimeout(() => setTlVisible(false), 2500);
    }, 4000);
    const id = setInterval(() => {
      setTlVisible(true);
      setTimeout(() => setTlVisible(false), 2500);
    }, ms);
    return () => { clearTimeout(initial); clearInterval(id); };
  }, [enabled, cornerTopLeftIntervalSeconds]);

  // Bottom-right corner flash (independent timing)
  useEffect(() => {
    if (!enabled || cornerBottomRightIntervalSeconds <= 0) { setBrVisible(false); return; }
    const ms = Math.max(5, cornerBottomRightIntervalSeconds) * 1000;
    const initial = setTimeout(() => {
      setBrVisible(true);
      setTimeout(() => setBrVisible(false), 2500);
    }, 9000);
    const id = setInterval(() => {
      setBrVisible(true);
      setTimeout(() => setBrVisible(false), 2500);
    }, ms);
    return () => { clearTimeout(initial); clearInterval(id); };
  }, [enabled, cornerBottomRightIntervalSeconds]);

  if (!enabled) return null;

  const cornerStyle: React.CSSProperties = {
    position: "absolute",
    color: "rgba(255, 255, 255, 0.6)",
    fontWeight: 600,
    fontSize: "0.65rem",
    letterSpacing: "0.02em",
    textShadow: "0 0 6px rgba(0,0,0,0.85)",
    whiteSpace: "nowrap",
    mixBlendMode: "difference",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    animation: "fitawrari-watermark-fade 2.5s ease-in-out",
    pointerEvents: "none",
  };

  return (
    <div
      className="absolute inset-0 z-30 pointer-events-none overflow-hidden select-none"
      aria-hidden="true"
      style={{ userSelect: "none", WebkitUserSelect: "none" }}
    >
      {/* Top-left corner flash */}
      {tlVisible && (
        <div style={{ ...cornerStyle, top: "10px", left: "12px" }}>
          {shortLabel}
        </div>
      )}

      {/* Bottom-right corner flash */}
      {brVisible && (
        <div style={{ ...cornerStyle, bottom: "10px", right: "12px" }}>
          {shortLabel}
        </div>
      )}

      {/* Center diagonal watermark */}
      {centerVisible && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%) rotate(-22deg)",
            color: "rgba(255, 255, 255, 0.18)",
            fontWeight: 800,
            fontSize: "clamp(1.1rem, 3.2vw, 2.2rem)",
            letterSpacing: "0.08em",
            textShadow: "0 0 10px rgba(0,0,0,0.7)",
            whiteSpace: "nowrap",
            mixBlendMode: "difference",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            animation: flashEnabled ? "fitawrari-watermark-fade 3s ease-in-out" : undefined,
            textAlign: "center",
          }}
        >
          {label}
        </div>
      )}

      <style>{`
        @keyframes fitawrari-watermark-fade {
          0%   { opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default ForensicWatermark;
